import { Button, Form, Input } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useUpdateAdminMutation } from 'app/services/edpApiV1/admins';
import React, { FC, useMemo, useState } from 'react';
import { AdminPatchRequest, AdminsDataPrepared } from 'shared/types/admins.types';
import { divideToPairs, generatePass } from 'shared/helpers/utils';
import useResponse from '../response-handle/useResponse';
import styles from './index.module.scss';

type TAdminUpdateFormProps = {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  admin: AdminsDataPrepared | null;
};

enum UpdateAdminPasswordFormFields {
  PASSWORD = 'password',
}

const UpdateAdminPasswordForm: FC<TAdminUpdateFormProps> = ({ setIsModalOpen, admin }) => {
  const [form] = useForm();
  const [password, setPassword] = useState<string>('');
  const [passGenerateLoading, setPassGenerateLoading] = useState<boolean>(false);

  const [
    updateAdminQuery,
    { data: updateResult, isError: isUpdateError, isSuccess: isUpdateSuccess, error: updateError },
  ] = useUpdateAdminMutation();

  useResponse({
    isUpdateError,
    isUpdateSuccess,
    updateError,
    updateResult,
  });

  const passRegExp = useMemo(() => {
    return new RegExp(
      `^(?!.*(${divideToPairs(admin?.email || '').join(
        '|',
      )}))(?=.*[A-Z])(?=.*\\d)(?=.*[@#$&*%])[A-Za-z\\d@#$&*%!^+-=~\\\`]{16,}$`,
    );
  }, [admin?.email]);

  const onFinish = (value: { password: string }) => {
    if (admin) {
      const requestData: AdminPatchRequest = {
        employeeId: admin.id,
        email: admin.email,
        role: admin.role?.id,
        faqModerator: admin.faqModerator ?? false,
        newsModerator: admin.newsModerator ?? false,
        pollModerator: admin.pollModerator ?? false,
        password: value.password,
        organizationIds: admin.organizationIds,
      };

      updateAdminQuery(requestData);
      Object.keys(form.getFieldsValue()).forEach(key => {
        form.setFieldValue(key, undefined);
      });
      setIsModalOpen(false);
    }
  };

  const generatePassword = async () => {
    setPassGenerateLoading(true);
    const newPass = await generatePass(16, { regExp: passRegExp });
    setPassGenerateLoading(false);

    form.setFieldValue(UpdateAdminPasswordFormFields.PASSWORD, newPass);
    form.validateFields();
  };

  return (
    <Form
      form={form}
      name="basic"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      style={{ maxWidth: 600 }}
      initialValues={{ remember: true }}
      onFinish={onFinish}
      autoComplete="off"
    >
      <Form.Item
        label="Введите новый пароль"
        name={UpdateAdminPasswordFormFields.PASSWORD}
        style={{ marginBottom: 10 }}
        rules={[
          { required: true, message: 'Введите пароль!' },
          {
            pattern: passRegExp,
            message: `Пароль должен быть от 16 символов, содержать заглавные и строчные буквы, цифры и, как минимум, один символ из перечисленных @#$&*%.
               Пароль не должен содержать два подряд идущих символа из логина.
               Текущая длина: ${password.length} символов.

              `,
          },
        ]}
      >
        <Input onChange={e => setPassword(e.target.value)} />
      </Form.Item>

      <Form.Item className={styles.button}>
        <div className={styles.buttons}>
          <Button onClick={generatePassword} disabled={passGenerateLoading}>
            Сгенерировать
          </Button>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};

export default UpdateAdminPasswordForm;
